import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Edit2, ExternalLink, FileUp, Package, Plus, Search, ShoppingCart, Trash2, X, Check, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

const EMPTY_EA = {
  slug: '',
  name: '',
  version: '',
  platform: 'MT5',
  status: 'draft',
  shortDescription: '',
  description: '',
  features: [],
  priceUsd: 0,
  isFree: true,
  requiresBrokerSignup: false,
  brokerName: '',
  brokerSignupUrl: '',
  buyUrl: '',
  trackingUrl: '',
  imageUrl: '',
  galleryImages: [],
  tags: [],
  fileName: '',
  fileMime: 'application/octet-stream',
  fileContentBase64: '',
  externalDownloadUrl: '',
  sortOrder: 0,
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return value.split(/\r?\n|,/).map((entry) => entry.trim()).filter(Boolean);
  }
  return [];
};

const toGalleryImages = (value) => toArray(value).filter((image) => image && typeof image === 'object' && image.src);

const normalizeEa = (item = {}) => ({
  ...EMPTY_EA,
  id: item.id,
  slug: item.slug || '',
  name: item.name || '',
  version: item.version || '',
  platform: item.platform || 'MT5',
  status: item.status || 'draft',
  shortDescription: item.short_description || item.shortDescription || '',
  description: item.description || '',
  features: toArray(item.features),
  priceUsd: Number(item.price_usd ?? item.priceUsd ?? 0),
  isFree: item.is_free ?? item.isFree ?? true,
  requiresBrokerSignup: item.requires_broker_signup ?? item.requiresBrokerSignup ?? false,
  brokerName: item.broker_name || item.brokerName || '',
  brokerSignupUrl: item.broker_signup_url || item.brokerSignupUrl || '',
  buyUrl: item.buy_url || item.buyUrl || '',
  trackingUrl: item.tracking_url || item.trackingUrl || '',
  imageUrl: item.image_url || item.imageUrl || '',
  galleryImages: toGalleryImages(item.gallery_images || item.galleryImages),
  tags: toArray(item.tags),
  fileName: item.file_name || item.fileName || '',
  fileMime: item.file_mime || item.fileMime || 'application/octet-stream',
  externalDownloadUrl: item.external_download_url || item.externalDownloadUrl || '',
  sortOrder: Number(item.sort_order ?? item.sortOrder ?? 0),
  downloadCount: Number(item.download_count || 0),
  hasUploadedFile: Boolean(item.has_uploaded_file),
  hasDownload: Boolean(item.has_download),
});

const slugify = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

export default function AdminEAStore() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EA);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await apiGet(API_ROUTES.admin.eaStore);
      setItems((data.items || []).map(normalizeEa));
    } catch (error) {
      toast.error('Failed to load EA store');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...EMPTY_EA, features: [], tags: [] });
    setCreating(true);
  };

  const openEdit = (item) => {
    setForm(normalizeEa(item));
    setEditing(item);
  };

  const closeModal = () => {
    setCreating(false);
    setEditing(null);
    setForm(EMPTY_EA);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('EA name is required');
    const payload = {
      ...form,
      slug: form.slug || slugify(form.name),
      priceUsd: Number(form.priceUsd || 0),
      sortOrder: Number(form.sortOrder || 0),
      features: form.features,
      tags: form.tags,
    };
    try {
      const res = await apiFetch(editing ? `${API_ROUTES.admin.eaStore}/${editing.id}` : API_ROUTES.admin.eaStore, {
        method: editing ? 'PATCH' : 'POST',
        body: payload,
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.detail || 'Save failed');
      }
      toast.success(editing ? 'EA updated' : 'EA added');
      closeModal();
      fetchItems();
    } catch (error) {
      toast.error(error.message || 'Failed to save EA');
    }
  };

  const deleteItem = async () => {
    if (!pendingDelete) return;
    try {
      const res = await apiFetch(`${API_ROUTES.admin.eaStore}/${pendingDelete.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('EA deleted');
      setPendingDelete(null);
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete EA');
    }
  };

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch = !q || item.name.toLowerCase().includes(q) || item.slug.toLowerCase().includes(q) || item.platform.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter]);

  const published = items.filter((item) => item.status === 'published').length;
  const freeCount = items.filter((item) => item.isFree).length;
  const paidCount = items.filter((item) => !item.isFree).length;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">EA Store Management</h1>
          <p className="text-sm text-gray-500 mt-1">Publish free, broker-gated, and paid Expert Advisors for the public store</p>
        </div>
        <button onClick={openCreate} className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm font-medium">
          <Plus size={16} /> Add EA
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Store EAs" value={items.length} sub={`${published} published`} />
        <SummaryCard label="Free" value={freeCount} sub="Instant or broker-gated" />
        <SummaryCard label="Paid" value={paidCount} sub="Buy button enabled" />
        <SummaryCard label="Downloads" value={items.reduce((sum, item) => sum + item.downloadCount, 0)} sub="Total public downloads" />
      </div>

      <div className="glass-card bg-dark-card p-4 mb-6 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="relative w-full md:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search EA name, slug, platform..." className="w-full pl-9 pr-4 py-2 bg-dark-surface border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40" />
        </div>
        <div className="flex items-center gap-2">
          {['all', 'published', 'draft'].map((status) => (
            <button key={status} onClick={() => setStatusFilter(status)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${statusFilter === status ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-gray-200'}`}>
              {status}
            </button>
          ))}
        </div>
      </div>

      {(creating || editing) && <EAStoreModal form={form} setForm={setForm} editing={Boolean(editing)} onSave={save} onClose={closeModal} />}

      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase">
                <th className="text-left px-4 py-3">EA</th>
                <th className="text-center px-4 py-3">Mode</th>
                <th className="text-left px-4 py-3">Access</th>
                <th className="text-left px-4 py-3">Tracking</th>
                <th className="text-center px-4 py-3">Downloads</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : !visibleItems.length ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">No EAs match the current filters</td></tr>
              ) : visibleItems.map((item) => (
                <tr key={item.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-300"><Package size={18} /></div>
                      <div>
                        <p className="text-gray-100 font-semibold">{item.name}</p>
                        <p className="text-xs text-gray-500 font-mono">/{item.slug} � {item.platform} {item.version}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${item.isFree ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{item.isFree ? 'Free' : `$${item.priceUsd.toFixed(2)}`}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {item.requiresBrokerSignup ? <span className="text-cyan-300">Broker signup: {item.brokerName || 'Required'}</span> : item.isFree ? 'Direct free download' : 'Buy URL'}
                    <p className="text-xs text-gray-600 mt-1">{item.hasDownload ? (item.hasUploadedFile ? 'Uploaded file ready' : 'External download URL') : 'No download file yet'}</p>
                  </td>
                  <td className="px-4 py-3">
                    {item.trackingUrl ? <a href={item.trackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"><ExternalLink size={13} /> Tracking link</a> : <span className="text-gray-600">Not set</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300">{item.downloadCount}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={item.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-3">
                      {item.status === 'published' && <Link to={`/ea-store/${item.slug}`} className="text-emerald-300 hover:text-emerald-200" title="View public page"><ExternalLink size={16} /></Link>}
                      <button onClick={() => openEdit(item)} className="text-blue-400 hover:text-blue-300" title="Edit EA"><Edit2 size={16} /></button>
                      <button onClick={() => setPendingDelete(item)} className="text-red-400 hover:text-red-300" title="Delete EA"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminConfirmDialog
        open={Boolean(pendingDelete)}
        tone="danger"
        title="Delete EA from store?"
        message={pendingDelete ? `${pendingDelete.name} will be removed from admin and public store. Uploaded file data for this EA will also be deleted.` : ''}
        confirmLabel="Delete EA"
        requireText={pendingDelete?.slug || ''}
        onConfirm={deleteItem}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function EAStoreModal({ form, setForm, editing, onSave, onClose }) {
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateName = (value) => setForm((prev) => ({ ...prev, name: value, slug: prev.slug || slugify(value) }));

  const handleImages = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const remaining = Math.max(0, 12 - (form.galleryImages || []).length);
    const picked = selected.slice(0, remaining);
    if (picked.length < selected.length) toast.error('Maximum 12 images per EA.');
    const images = [];
    for (const file of picked) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 450000) {
        toast.error(`${file.name} is too large. Keep each image under 450KB.`);
        continue;
      }
      const dataUrl = await fileToDataUrl(file);
      images.push({ name: file.name, mime: file.type || 'image/jpeg', src: dataUrl });
    }
    if (images.length) {
      setForm((prev) => {
        const next = [...(prev.galleryImages || []), ...images].slice(0, 12);
        return { ...prev, galleryImages: next, imageUrl: prev.imageUrl || next[0]?.src || '' };
      });
    }
  };

  const removeImage = (index) => {
    setForm((prev) => {
      const next = (prev.galleryImages || []).filter((_, i) => i !== index);
      return { ...prev, galleryImages: next, imageUrl: prev.imageUrl?.startsWith('data:image/') ? (next[0]?.src || '') : prev.imageUrl };
    });
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 750000) {
      toast.error('File is too large for database upload. Use External Download URL instead.');
      return;
    }
    const base64 = await fileToBase64(file);
    setForm((prev) => ({ ...prev, fileName: file.name, fileMime: file.type || 'application/octet-stream', fileContentBase64: base64 }));
  };

  const featureText = (form.features || []).join('\n');
  const tagText = (form.tags || []).join(', ');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-card border border-dark-border rounded-xl w-full max-w-5xl max-h-[92vh] overflow-hidden shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-dark-border px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-gray-100">{editing ? `Edit ${form.name}` : 'Add EA to Store'}</h2>
            <p className="text-xs text-gray-500 mt-1">Control public listing, broker-gated free downloads, paid buy links, and tracking proof.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-200 rounded-lg p-1" title="Close"><X size={20} /></button>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-150px)] px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-5">
            <Section title="EA Identity">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField label="EA Name" value={form.name} placeholder="EverestPeak" onChange={updateName} />
                <TextField label="Slug" value={form.slug} placeholder="everestpeak" onChange={(v) => update('slug', slugify(v))} />
                <TextField label="Version" value={form.version} placeholder="1.2" onChange={(v) => update('version', v)} />
                <SelectField label="Platform" value={form.platform} onChange={(v) => update('platform', v)} options={['MT5', 'MT4', 'MT4/MT5']} />
              </div>
              <div className="mt-4"><TextField label="Short Description" value={form.shortDescription} placeholder="Gold scalping EA with tracked live performance" onChange={(v) => update('shortDescription', v)} /></div>
              <div className="mt-4"><TextArea label="Full Description" value={form.description} rows={4} onChange={(v) => update('description', v)} /></div>
            </Section>

            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">Store Preview</p>
              <p className="text-xl font-bold text-gray-100 mt-3">{form.name || 'EA name'}</p>
              <p className="text-xs text-gray-500 font-mono mt-1">/ea-store/{form.slug || 'ea-slug'}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <PreviewMetric label="Price" value={form.isFree ? 'Free' : `$${Number(form.priceUsd || 0).toFixed(2)}`} />
                <PreviewMetric label="Access" value={form.requiresBrokerSignup ? 'Broker gated' : 'Direct'} />
                <PreviewMetric label="Status" value={form.status} />
                <PreviewMetric label="Images" value={(form.galleryImages || []).length} />
              </div>
              <a href="/ea-store" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"><ExternalLink size={12} /> Public store</a>
            </div>
          </div>

          <Section title="Pricing & Access">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <ToggleField label="Free EA" checked={form.isFree} onChange={(checked) => update('isFree', checked)} icon={Download} />
              <MoneyField label="Price USD" value={form.priceUsd} disabled={form.isFree} onChange={(v) => update('priceUsd', Number(v || 0))} />
              <ToggleField label="Published" checked={form.status === 'published'} onChange={(checked) => update('status', checked ? 'published' : 'draft')} icon={Power} />
              <NumberField label="Sort Order" value={form.sortOrder} onChange={(v) => update('sortOrder', Number(v || 0))} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <TextField label="Buy URL" value={form.buyUrl} placeholder="Payment/contact URL for paid EA" onChange={(v) => update('buyUrl', v)} />
              <TextField label="Tracking Link" value={form.trackingUrl} placeholder="https://trackmyfxea.com/shared/..." onChange={(v) => update('trackingUrl', v)} />
            </div>
          </Section>

          <Section title="Broker-Gated Free Download">
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_1fr] gap-4">
              <ToggleField label="Require broker signup" checked={form.requiresBrokerSignup} onChange={(checked) => update('requiresBrokerSignup', checked)} icon={Check} />
              <TextField label="Broker Name" value={form.brokerName} placeholder="Vantage Markets" onChange={(v) => update('brokerName', v)} />
              <TextField label="Broker Signup Link" value={form.brokerSignupUrl} placeholder="Your affiliate signup URL" onChange={(v) => update('brokerSignupUrl', v)} />
            </div>
          </Section>

          <Section title="Download File">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-dashed border-dark-border bg-dark-surface/50 p-4">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><FileUp size={16} /> Upload EA file</label>
                <input type="file" accept=".ex4,.ex5,.mq4,.mq5,.zip" onChange={(e) => handleFile(e.target.files?.[0])} className="block w-full text-xs text-gray-400 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-500 file:px-3 file:py-2 file:text-white file:text-xs file:font-semibold" />
                <p className="text-xs text-gray-600 mt-2">For larger files, paste an external download URL instead.</p>
                {(form.fileName || form.hasUploadedFile) && <p className="text-xs text-emerald-300 mt-2">{form.fileName || 'Existing uploaded file kept'}</p>}
              </div>
              <TextField label="External Download URL" value={form.externalDownloadUrl} placeholder="Optional direct download URL" onChange={(v) => update('externalDownloadUrl', v)} />
            </div>
          </Section>

          <Section title="Result Screenshots / Gallery">
            <div className="rounded-lg border border-dashed border-dark-border bg-dark-surface/50 p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><FileUp size={16} /> Upload multiple result images</label>
              <input type="file" accept="image/*" multiple onChange={(e) => handleImages(e.target.files)} className="block w-full text-xs text-gray-400 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-500 file:px-3 file:py-2 file:text-white file:text-xs file:font-semibold" />
              <p className="text-xs text-gray-600 mt-2">Up to 12 images. Keep each image under 450KB for fast public loading.</p>
            </div>
            {!!form.galleryImages?.length && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {form.galleryImages.map((image, index) => (
                  <div key={`${image.name}-${index}`} className="relative rounded-lg border border-dark-border overflow-hidden bg-dark-surface group">
                    <img src={image.src} alt={image.name || `EA result ${index + 1}`} className="w-full h-28 object-cover" />
                    <button type="button" onClick={() => removeImage(index)} className="absolute top-2 right-2 rounded-md bg-black/70 p-1 text-white opacity-0 group-hover:opacity-100" title="Remove image"><X size={13} /></button>
                    <p className="px-2 py-1 text-[10px] text-gray-500 truncate">{index === 0 ? 'Main image � ' : ''}{image.name || `Image ${index + 1}`}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Marketing Details">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TextField label="Image URL" value={form.imageUrl} placeholder="Screenshot or product image URL" onChange={(v) => update('imageUrl', v)} />
              <TextField label="Tags" value={tagText} placeholder="gold, scalping, low risk" onChange={(v) => update('tags', v.split(',').map((x) => x.trim()).filter(Boolean))} />
            </div>
            <div className="mt-4">
              <TextArea label="Features" value={featureText} rows={5} placeholder="One feature per line" onChange={(v) => update('features', v.split(/\r?\n/).map((x) => x.trim()).filter(Boolean))} />
            </div>
          </Section>
        </div>

        <div className="border-t border-dark-border px-6 py-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-gray-300 hover:text-white text-sm font-medium">Cancel</button>
          <button onClick={onSave} className="px-5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-semibold">{editing ? 'Save Changes' : 'Add EA'}</button>
        </div>
      </div>
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Section({ title, children }) {
  return <div className="rounded-lg border border-dark-border bg-dark-surface/30 p-4"><p className="text-xs text-gray-400 uppercase font-semibold mb-3">{title}</p>{children}</div>;
}
function TextField({ label, value, placeholder, onChange }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label><input type="text" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40" /></div>;
}
function TextArea({ label, value, placeholder, rows = 3, onChange }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label><textarea rows={rows} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40 resize-y" /></div>;
}
function SelectField({ label, value, options, onChange }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label><select value={value} onChange={(e) => onChange(e.target.value)} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40">{options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>;
}
function NumberField({ label, value, onChange }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label><input type="number" value={value ?? 0} onChange={(e) => onChange(e.target.value)} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40" /></div>;
}
function MoneyField({ label, value, disabled, onChange }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label><div className={`flex items-center rounded-lg border border-dark-border bg-dark-surface ${disabled ? 'opacity-50' : ''}`}><span className="text-gray-500 px-3">$</span><input disabled={disabled} type="number" step="0.01" min="0" value={value ?? 0} onChange={(e) => onChange(e.target.value)} className="bg-transparent px-0 py-2 pr-3 text-sm text-gray-200 w-full focus:outline-none" /></div></div>;
}
function ToggleField({ label, checked, onChange, icon: Icon }) {
  return <button type="button" onClick={() => onChange(!checked)} className={`h-[64px] rounded-lg border px-3 text-sm font-medium flex items-center justify-center gap-2 transition ${checked ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300' : 'border-dark-border bg-dark-surface text-gray-400'}`}><Icon size={15} />{label}</button>;
}
function PreviewMetric({ label, value }) {
  return <div className="rounded-lg bg-dark-surface border border-dark-border p-3"><p className="text-[10px] uppercase text-gray-600 font-semibold">{label}</p><p className="text-sm text-gray-200 font-semibold mt-1 truncate">{value}</p></div>;
}
function SummaryCard({ label, value, sub }) {
  return <div className="glass-card bg-dark-card p-4"><p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p><p className="text-2xl font-bold text-gray-100 mt-2">{value}</p><p className="text-xs text-gray-600 mt-1">{sub}</p></div>;
}
function StatusBadge({ status }) {
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs ${status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>{status === 'published' ? 'Published' : 'Draft'}</span>;
}


